import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, ShieldAlert, Ban } from "lucide-react";
import { CloudIcon, LangIcon, ProviderIcon } from "@/components/brand-icons";
import { EnvironmentPicker, EnvParamsEditor, CostEstimate } from "@/components/environments";
import { cn } from "@/lib/utils";
import { VerifiedIcon } from "@/components/catalog-card";

export const WIZARD_STEPS = ["Cloud & IaC", "Naming & Region", "Environments & Tags", "Resources", "Review"];
export const CI_OPTIONS = [
  { key: "github", label: "GitHub Actions" },
  { key: "gitlab", label: "GitLab CI" },
  { key: "azure_devops", label: "Azure Pipelines" },
  { key: "none", label: "None" },
];
const CLOUD_DOT = { azure: "#0078D4", aws: "#FF9900", gcp: "#4285F4" };

const tileClass = (active, extra = "") =>
  cn("rounded-md border text-left transition-colors", active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400", extra);

export function WizardProgress({ step }) {
  return (
    <ol className="mb-8 flex items-center gap-2 text-xs" data-testid="wizard-steps">
      {WIZARD_STEPS.map((s, i) => (
        <li key={s} className={cn("flex items-center gap-2", i > 0 && "before:h-px before:w-6 before:bg-slate-200")}>
          <span className={cn(
            "grid h-6 w-6 place-items-center rounded-full border font-mono-tight text-[11px]",
            i < step && "border-slate-900 bg-slate-900 text-white",
            i === step && "border-slate-900 text-slate-900",
            i > step && "border-slate-200 text-slate-400",
          )}>{i + 1}</span>
          <span className={cn("uppercase tracking-widest", i === step ? "text-slate-900" : "text-slate-400")}>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function StepCloud({ form, rules, setF }) {
  const langOptions = form.cloud === "azure"
    ? [{ key: "terraform", label: "Terraform" }, { key: "bicep", label: "Bicep" }]
    : [{ key: "terraform", label: "Terraform" }];
  const pickCloud = (c) => {
    setF("cloud", c);
    if (c !== "azure") setF("language", "terraform");
    setF("region", rules.allowed_regions?.[c]?.[0] || "");
  };
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Cloud provider</Label>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {["azure", "aws", "gcp"].map((c) => (
            <button key={c} type="button" data-testid={`cloud-${c}`} onClick={() => pickCloud(c)} className={tileClass(form.cloud === c, "flex items-center justify-between px-4 py-4")}>
              <span className="flex items-center gap-3">
                <CloudIcon cloud={c} className="h-6 w-6" />
                <span className="font-display text-lg font-semibold uppercase">{c}</span>
              </span>
              <span className="h-2 w-2 rounded-full" style={{ background: CLOUD_DOT[c] }} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">IaC language</Label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {langOptions.map((l) => (
            <button key={l.key} type="button" data-testid={`lang-${l.key}`} onClick={() => setF("language", l.key)} className={tileClass(form.language === l.key, "flex items-center gap-3 px-4 py-4")}>
              <LangIcon language={l.key} className="h-6 w-6" />
              <span className="font-display text-base font-semibold">{l.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">CI/CD pipeline</Label>
        <div className="mt-3 grid grid-cols-4 gap-3">
          {CI_OPTIONS.map((c) => (
            <button key={c.key} type="button" data-testid={`ci-${c.key}`} onClick={() => setF("ci_platform", c.key)} className={tileClass(form.ci_platform === c.key, "flex items-center gap-2 px-3 py-3 text-sm")}>
              {c.key === "none" ? <Ban className="h-4 w-4 opacity-60" /> : <ProviderIcon provider={c.key} className="h-4 w-4" />}
              <span className="font-display font-semibold">{c.label}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 font-mono-tight text-[11px] text-slate-500">Adds a pipeline with the Toastern compliance gate, plan on PR and apply on main.</p>
      </div>
    </div>
  );
}

export function StepNaming({ form, rules, setF }) {
  const allowedRegions = rules.allowed_regions?.[form.cloud] || [];
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Project name</Label>
        <Input data-testid="input-name" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.name} onChange={(e) => setF("name", e.target.value)} />
        <p className="mt-2 font-mono-tight text-[11px] text-slate-500">
          pattern <span className="text-slate-700">{rules.naming_pattern}</span> · prefix <span className="text-slate-700">{rules.naming_prefix}</span>
        </p>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Region</Label>
        <Select value={form.region} onValueChange={(v) => setF("region", v)}>
          <SelectTrigger data-testid="select-region" className="mt-2 h-10 border-slate-200">
            <SelectValue placeholder="Pick a region" />
          </SelectTrigger>
          <SelectContent>
            {allowedRegions.map((r) => (
              <SelectItem key={r} value={r} data-testid={`region-${r}`}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
        <Textarea data-testid="input-description" className="mt-2 border-slate-200" value={form.description} onChange={(e) => setF("description", e.target.value)} rows={3} />
      </div>
    </div>
  );
}

export function StepEnvironments({ form, rules, setF, setTag }) {
  return (
    <div className="space-y-6">
      <EnvironmentPicker allowed={rules.allowed_environments || []} value={form.environments} onChange={(v) => setF("environments", v)} />
      {rules.mandatory_tags.filter((t) => t !== "environment").map((t) => (
        <div key={t}>
          <Label className="text-xs uppercase tracking-widest text-slate-500">{t}</Label>
          <Input
            data-testid={`tag-${t}`}
            className="mt-2 h-10 border-slate-200 font-mono-tight text-sm"
            value={form.tags[t] || ""}
            onChange={(e) => setTag(t, e.target.value)}
            placeholder={t === "environment" ? rules.allowed_environments.join(" · ") : ""}
          />
        </div>
      ))}
    </div>
  );
}

export function StepResources({ form, cloudCatalog, toggleResource, setF }) {
  return (
    <div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {cloudCatalog.map((r) => (
          <label
            key={r.key}
            className={cn("flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors", form.resources.includes(r.key) ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400")}
            data-testid={`res-${r.key}`}
          >
            <Checkbox checked={form.resources.includes(r.key)} onCheckedChange={() => toggleResource(r.key)} className="mt-1" />
            <div>
              <div className="flex items-center gap-1.5 font-display text-sm font-semibold text-slate-900">{r.name} <VerifiedIcon verified={r.verified} className="h-3.5 w-3.5" /></div>
              <p className="mt-1 text-xs leading-relaxed text-slate-600">{r.description}</p>
              <p className="mt-2 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{r.category}</p>
            </div>
          </label>
        ))}
      </div>
      <EnvParamsEditor resources={form.resources} catalog={cloudCatalog} envs={form.environments} overrides={form.env_overrides} onChange={(v) => setF("env_overrides", v)} />
    </div>
  );
}

function ReviewField({ label, value, testid }) {
  return (
    <div>
      <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <div className={cn("font-medium", label === "cloud" && "uppercase")} data-testid={testid}>{value}</div>
    </div>
  );
}

export function StepReview({ form, rules, cloudCatalog, preview }) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-6" data-testid="review-summary">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <ReviewField label="name" value={form.name} />
          <ReviewField label="cloud" value={form.cloud} />
          <ReviewField label="iac" value={form.language} />
          <ReviewField label="ci/cd" value={CI_OPTIONS.find((c) => c.key === form.ci_platform)?.label} testid="review-ci" />
          <ReviewField label="region" value={form.region} />
          <ReviewField label="environments" value={form.environments.join(", ") || "—"} testid="review-envs" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {form.resources.map((r) => (
            <Badge key={r} className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{r}</Badge>
          ))}
        </div>
      </div>

      <CostEstimate cloud={form.cloud} region={form.region} envs={form.environments} resources={form.resources} catalog={cloudCatalog} overrides={form.env_overrides} budgets={rules.budgets || {}} />

      {preview && (
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="review-compliance">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold">Compliance</h3>
            <span className={cn("inline-flex items-center gap-1 text-sm", preview.compliance.compliant ? "text-emerald-600" : "text-amber-600")}>
              {preview.compliance.compliant ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
              {preview.compliance.score}%
            </span>
          </div>
          <div className="space-y-2">
            {preview.compliance.checks.map((c) => (
              <div key={c.id} className="flex items-start justify-between rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
                <div>
                  <div className="font-medium text-slate-800">{c.label}</div>
                  <div className="text-xs text-slate-500">{c.detail}</div>
                </div>
                <span className={cn("font-mono-tight text-[10px] uppercase tracking-widest", c.status === "pass" ? "text-emerald-600" : "text-amber-600")}>{c.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
